import { Lease } from '../../types/leases';
import { TenantKind } from '../../types/Enums';
import { Tenant } from '../../types/tenants';
import { naturalCompare } from '../../Helpers/naturalSort';
import { leaseInvolvesTenant, leaseObjectLabel, leaseStatus, todayStockholm } from '../../Helpers/leaseUtils';

/**
 * Pure view logic of Hyresgäster (/tenants): which tenants a filter shows,
 * what they rent now and the sort. The page only wires these to state and the URL.
 */

export interface TenantFilters {
    /** Empty = every building. */
    buildingKeys: string[];
    /** Empty = both kinds. */
    kinds: TenantKind[];
    search: string;
}

export interface TenantRow {
    tenant: Tenant;
    /** Active and upcoming leases the tenant is on (primary or co-tenant), in the chosen buildings. */
    currentLeases: Lease[];
}

/** The Typ filter's choices, in the order shown. */
export const TENANT_KIND_FILTERS: TenantKind[] = [TenantKind.Person, TenantKind.Company];

const matchesSearch = (tenant: Tenant, needle: string): boolean =>
    !needle || [tenant.name, tenant.orgNumber, tenant.email, tenant.phone]
        .some(text => (text || '').toLowerCase().includes(needle));

/**
 * The tenants a filter shows, each with what they rent now. With buildings
 * chosen, a tenant is listed when they have or had a lease there (primary or
 * co-tenant); without, every tenant is, renting something or not.
 */
export const filterTenants = (
    tenants: Tenant[],
    leases: Lease[],
    filters: TenantFilters,
    today: string = todayStockholm()
): TenantRow[] => {
    const buildings = new Set(filters.buildingKeys);
    const kinds = new Set(filters.kinds);
    const needle = filters.search.trim().toLowerCase();
    const inBuildings = buildings.size === 0 ? leases : leases.filter(lease => buildings.has(lease.buildingKey));

    const rows: TenantRow[] = [];
    for (const tenant of tenants) {
        if (kinds.size > 0 && !kinds.has(tenant.kind)) continue;
        if (!matchesSearch(tenant, needle)) continue;
        const theirs = inBuildings.filter(lease => leaseInvolvesTenant(lease, tenant.key));
        if (buildings.size > 0 && theirs.length === 0) continue;
        rows.push({ tenant, currentLeases: theirs.filter(lease => leaseStatus(lease, today) !== 'ended') });
    }
    return rows;
};

/** "P12, Lgh 1001": the objects of the current leases, in natural order. */
export const currentObjectsLabel = (row: TenantRow): string =>
    row.currentLeases.map(leaseObjectLabel).sort(naturalCompare).join(', ');

export type TenantSortKey = 'name' | 'objects';

export interface TenantSort {
    key: TenantSortKey;
    direction: 'asc' | 'desc';
}

export const DEFAULT_TENANT_SORT: TenantSort = { key: 'name', direction: 'asc' };

const compareBy = (key: TenantSortKey) => (a: TenantRow, b: TenantRow): number => {
    switch (key) {
        case 'name': return naturalCompare(a.tenant.name, b.tenant.name);
        case 'objects': {
            // Tenants renting nothing go last whichever way the column runs.
            const aLabel = currentObjectsLabel(a);
            const bLabel = currentObjectsLabel(b);
            if (!aLabel || !bLabel) return aLabel ? -1 : bLabel ? 1 : 0;
            return naturalCompare(aLabel, bLabel);
        }
    }
};

/** Sorted copy: the chosen column, then the name for equal rows. */
export const sortTenantRows = (rows: TenantRow[], sort: TenantSort): TenantRow[] => {
    const primary = compareBy(sort.key);
    const byName = compareBy('name');
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const emptyA = sort.key === 'objects' && a.currentLeases.length === 0;
        const emptyB = sort.key === 'objects' && b.currentLeases.length === 0;
        if (emptyA !== emptyB) return emptyA ? 1 : -1;
        return primary(a, b) * sign || byName(a, b);
    });
};

/** Clicking a header: the same column flips direction, a new one starts ascending. */
export const nextTenantSort = (current: TenantSort, key: TenantSortKey): TenantSort =>
    current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' };

/**
 * The tenant's leases in the pane: current ones (upcoming before active, then
 * by start), and the ended ones newest end first.
 */
export const tenantLeaseGroups = (
    leases: Lease[],
    tenantKey: string,
    today: string = todayStockholm()
): { current: Lease[]; ended: Lease[] } => {
    const theirs = leases.filter(lease => leaseInvolvesTenant(lease, tenantKey));
    const current = theirs
        .filter(lease => leaseStatus(lease, today) !== 'ended')
        .sort((a, b) => b.startDate.localeCompare(a.startDate));
    const ended = theirs
        .filter(lease => leaseStatus(lease, today) === 'ended')
        .sort((a, b) => (b.endDate ?? '').localeCompare(a.endDate ?? ''));
    return { current, ended };
};
